import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserCheck, UserX, ClipboardList, StickyNote, Paperclip, Upload, X, ListChecks, Check, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ProgressBar } from "@/components/ReportProgress";

function ApplicantReports({ reports, id }) {
  const mine = reports.filter((r) => r.driver_id === id && r.status !== "cancelled");
  if (mine.length === 0) return <span className="text-xs text-slate-400 italic" data-testid={`applicant-reports-none-${id}`}>No reports requested</span>;
  const done = mine.filter((r) => r.status === "completed").length;
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={`applicant-reports-${id}`}>
      <Badge className={`border font-sans ${done === mine.length ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-sky-50 text-sky-800 border-sky-200"}`} data-testid={`applicant-reports-summary-${id}`}>{done === mine.length ? "All reports complete" : `${done}/${mine.length} reports complete`}</Badge>
      {mine.map((r) => (
        <div key={r.id} className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-slate-600 uppercase">{(r.report_types || []).join(" · ")}</span>
          <ProgressBar r={r} compact />
        </div>
      ))}
    </div>
  );
}

function ApplicantNote({ d, onSaved }) {
  const [val, setVal] = useState(d.applicant_note || "");
  const [editing, setEditing] = useState(false);
  const save = async () => {
    setEditing(false);
    if ((val || "") === (d.applicant_note || "")) return;
    try { await api.patch(`/drivers/${d.id}`, { applicant_note: val.slice(0, 500) }); toast.success("Note saved"); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!editing) return (
    <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(true); }} className={`w-full text-left text-xs rounded px-2 py-1 border border-dashed ${d.applicant_note ? "border-amber-200 bg-white text-slate-700" : "border-slate-300 text-slate-400 hover:text-slate-600"}`} data-testid={`applicant-note-${d.id}`}>
      <StickyNote className="inline h-3 w-3 mr-1 -mt-0.5" />{d.applicant_note || "Add interview note…"}
    </button>
  );
  return (
    <textarea autoFocus rows={2} maxLength={500} value={val} onChange={(e) => setVal(e.target.value)} onBlur={save} onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.target.blur(); } if (e.key === "Escape") { setVal(d.applicant_note || ""); setEditing(false); } }}
      placeholder="Interview notes, references called, start date discussed… (Enter to save)" className="w-full text-xs rounded border border-amber-300 bg-white p-2 focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid={`applicant-note-input-${d.id}`} />
  );
}

const DOC_KINDS = ["Application", "References", "Interview sheet", "Resume", "Other"];

function ApplicantDocs({ d, onChanged }) {
  const [kind, setKind] = useState("Application");
  const [busy, setBusy] = useState(false);
  const docs = d.applicant_docs || [];
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
      await api.post(`/drivers/${d.id}/applicant-docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind} attached`); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const open = async (doc) => {
    try { const res = await api.get(`/drivers/${d.id}/applicant-docs/${doc.id}`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank", "noopener"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (doc) => {
    if (!window.confirm(`Remove ${doc.file_name}?`)) return;
    try { await api.delete(`/drivers/${d.id}/applicant-docs/${doc.id}`); onChanged?.(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()} data-testid={`applicant-docs-${d.id}`}>
      {docs.map((doc) => (
        <span key={doc.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white pl-2 pr-1 py-0.5 text-xs" data-testid={`applicant-doc-${doc.id}`}>
          <Paperclip className="h-3 w-3 text-slate-400" />
          <button type="button" onClick={() => open(doc)} className="text-blue-700 hover:underline" title={`${doc.file_name} · ${doc.uploaded_by}`} data-testid={`applicant-doc-open-${doc.id}`}><b>{doc.kind}</b> · {doc.file_name}</button>
          <button type="button" onClick={() => remove(doc)} className="rounded-full p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-700" aria-label="Remove" data-testid={`applicant-doc-remove-${doc.id}`}><X className="h-3 w-3" /></button>
        </span>
      ))}
      <span className="inline-flex items-center rounded-full border border-dashed border-slate-300 bg-white text-xs overflow-hidden">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="bg-transparent pl-2 pr-1 py-0.5 text-slate-600 focus:outline-none" data-testid={`applicant-doc-kind-${d.id}`}>{DOC_KINDS.map((k) => <option key={k}>{k}</option>)}</select>
        <label className="inline-flex items-center gap-1 px-2 py-0.5 text-blue-700 cursor-pointer hover:bg-slate-50 border-l border-slate-200" data-testid={`applicant-doc-upload-${d.id}`}><Upload className="h-3 w-3" />{busy ? "Uploading…" : "Attach"}<input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} /></label>
      </span>
    </div>
  );
}

function HiringChecklist({ d, data, onChanged }) {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const complete = data.done === data.total;
  const toggle = async (item) => {
    try {
      const body = item.manual ? { done: null } : { done: !item.done };
      await api.post(`/drivers/${d.id}/hiring-checklist/${item.key}`, body);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-2" onClick={(e) => e.stopPropagation()} data-testid={`hiring-checklist-${d.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${complete ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`} data-testid={`hiring-checklist-toggle-${d.id}`}>
        <ListChecks className="h-3.5 w-3.5" />391.21 / 391.23 checklist · {data.done}/{data.total}{complete ? " · ready to hire" : ""}{open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {open && (
        <ul className="mt-1.5 grid sm:grid-cols-2 gap-x-6 gap-y-1 rounded-md border border-slate-200 bg-white p-2.5 text-xs" data-testid={`hiring-checklist-items-${d.id}`}>
          {data.items.map((it) => (
            <li key={it.key} className="flex items-center gap-2">
              <button type="button" onClick={() => toggle(it)} title={it.manual ? `Marked by ${it.by} · click to return to auto-detect` : it.auto ? "Detected from records · click to override" : "Click to mark done"} className={`h-4 w-4 shrink-0 rounded border flex items-center justify-center ${it.done ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 bg-white"}`} data-testid={`hiring-check-${d.id}-${it.key}`}>{it.done && <Check className="h-3 w-3" />}</button>
              <span className={it.done ? "text-slate-700" : "text-slate-900 font-medium"}>{it.label}</span>
              <span className="font-mono text-[10px] text-slate-400">{it.reg}</span>
              {it.manual && <span className="text-[10px] text-slate-400 italic">manual</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ApplicantsSection({ applicants, reports = [], checklists = {}, isAdmin, customerName, onOpen, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [warn, setWarn] = useState(null);
  if (applicants.length === 0) return null;
  const decide = async (d, decision, force = false) => {
    if (decision === "not_hired" && !window.confirm(`Mark ${d.first_name} ${d.last_name} as not hired? Their records move to the Retention folder.`)) return;
    const open = (checklists[d.id]?.items || []).filter((i) => !i.done);
    if (decision === "hired" && !force && open.length) { setWarn({ d, open }); return; }
    setWarn(null);
    setBusy(d.id);
    try {
      await api.post(`/drivers/${d.id}/hire-decision`, null, { params: { decision } });
      toast.success(decision === "hired" ? `${d.first_name} ${d.last_name} is now an active driver` : `${d.first_name} ${d.last_name} marked not hired · moved to retention`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  return (
    <>
    <AlertDialog open={!!warn} onOpenChange={(o) => !o && setWarn(null)}>
      <AlertDialogContent data-testid="hire-warning-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" />Hiring checklist not complete</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-slate-600 space-y-2">
              <p>{warn?.d.first_name} {warn?.d.last_name} still has {warn?.open.length} open item{warn?.open.length === 1 ? "" : "s"} under 49 CFR 391.21 / 391.23:</p>
              <ul className="list-disc pl-5 space-y-0.5" data-testid="hire-warning-items">{warn?.open.map((i) => <li key={i.key}>{i.label} <span className="font-mono text-[10px] text-slate-400">{i.reg}</span></li>)}</ul>
              <p>You can hire anyway and finish these items from the driver's file; the gaps will show in Missing compliance until resolved.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="hire-warning-cancel">Go back</AlertDialogCancel>
          <AlertDialogAction className="bg-emerald-700 hover:bg-emerald-800" onClick={() => decide(warn.d, "hired", true)} data-testid="hire-warning-anyway">Hire anyway</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <Card className="mb-4 border-amber-300 bg-amber-50/40" data-testid="applicants-section">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-amber-200 text-amber-900 font-semibold">
        <ClipboardList className="h-4 w-4" />Applicants · pending hire decision
        <span className="text-xs font-mono opacity-70" data-testid="applicants-count">({applicants.length})</span>
        <span className="ml-auto text-xs font-normal text-amber-800">Not in random pool or alerts until hired</span>
      </div>
      <ul className="divide-y divide-amber-100">
        {applicants.map((d) => (
          <li key={d.id} className="px-4 py-2.5 hover:bg-amber-50 cursor-pointer" onClick={() => onOpen(d)} data-testid={`applicant-row-${d.id}`}>
            <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-900">{d.first_name} {d.last_name}{d.is_cdl !== false && <Badge className="ml-2 bg-blue-50 text-blue-700 border border-blue-200 font-sans">CDL</Badge>}</div>
              <div className="text-xs text-slate-500 font-mono">{d.license_number || "—"}{d.license_state ? ` · ${d.license_state}` : ""}{isAdmin && <span className="ml-2 font-sans text-blue-700">{customerName(d.customer_id)}</span>}</div>
            </div>
            <ApplicantReports reports={reports} id={d.id} />
            <Badge variant="outline" className="border-amber-300 bg-amber-100 text-amber-900">Applicant</Badge>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" disabled={busy === d.id} onClick={() => decide(d, "hired")} data-testid={`applicant-hire-${d.id}`}><UserCheck className="h-4 w-4 mr-1" />Hired</Button>
              <Button size="sm" variant="outline" className="border-slate-300 text-slate-700" disabled={busy === d.id} onClick={() => decide(d, "not_hired")} data-testid={`applicant-not-hired-${d.id}`}><UserX className="h-4 w-4 mr-1" />Not hired</Button>
            </div>
            </div>
            <div className="mt-2 sm:max-w-2xl"><ApplicantNote d={d} onSaved={onChanged} /></div>
            <ApplicantDocs d={d} onChanged={onChanged} />
            <HiringChecklist d={d} data={checklists[d.id]} onChanged={onChanged} />
          </li>
        ))}
      </ul>
    </Card>
    </>
  );
}
